import {csrfToken} from './api';

/**
 * Issue #977 FenzSafe summary slice 2: the owner-only, UNVERIFIED AI summary draft request for ONE
 * accepted document. Self-contained like fenzsafeChunkedUpload.ts: it issues its own `fetch` and
 * never goes through fenzsafeStore/fenzsafeBackendAdapter, so it must be called only when the real
 * backend adapter is bound (the caller passes `adapterReal`, from isFenzSafeAdapterReal()).
 *
 * Nothing here stores the draft anywhere: no store snapshot, browser storage, analytics, console or
 * error reporting. The server's `message` text is never surfaced; only the closed map below is.
 */

export const SUMMARY_MAX_STATEMENTS = 40;
export const SUMMARY_MAX_WARNINGS = 12;
export const SUMMARY_MAX_STATEMENT_CHARS = 500;
export const SUMMARY_MAX_EXCERPT_CHARS = 300;
export const SUMMARY_MAX_WARNING_CHARS = 300;
export const SUMMARY_MAX_NOTICE_CHARS = 1000;
export const SUMMARY_MAX_PAGE = 500;
export const UNVERIFIED_CITATION = 'unverified_model_citation';

export const SUMMARY_UNVERIFIABLE_CODE = 'fenzsafe_summary.unverifiable_response';
export const SUMMARY_UNAVAILABLE_CODE = 'fenzsafe_summary.local_unavailable';
export const SUMMARY_UNKNOWN_CODE = 'fenzsafe_summary.unknown_error';

/** Closed code -> message contract. Unknown codes use SUMMARY_UNKNOWN_CODE; server text is never shown. */
export const SUMMARY_ERROR_MESSAGES: Readonly<Record<string, string>> = Object.freeze({
  'fenzsafe_summary.sign_in_required': 'Your sign-in could not be verified. Sign in again, then return here.',
  'fenzsafe_summary.not_available': 'The summary request could not be completed.',
  'fenzsafe_summary.invalid_request': 'The summary request was not valid.',
  'fenzsafe_summary.not_found': 'The document could not be found.',
  'fenzsafe_summary.document_not_ready': 'This document has not finished its safety check yet.',
  'fenzsafe_summary.storage_unavailable': 'The document could not be read right now.',
  'fenzsafe_summary.too_large': 'This document is too large to summarize.',
  'fenzsafe_summary.unsupported_type': 'Only PDF documents can be summarized.',
  'fenzsafe_summary.too_long': 'This document is too long to summarize.',
  'fenzsafe_summary.allowance_exhausted': 'The summary allowance has been used up for now. Try again later.',
  'fenzsafe_summary.reading_failed': 'The summary could not be prepared. Nothing was saved.',
  [SUMMARY_UNVERIFIABLE_CODE]: 'The summary response could not be checked, so it was not shown.',
  [SUMMARY_UNAVAILABLE_CODE]: 'Summaries are not available here right now.',
  [SUMMARY_UNKNOWN_CODE]: 'The summary request could not be completed.',
});

export function summaryErrorMessage(code: string): string {
  return SUMMARY_ERROR_MESSAGES[code] ?? SUMMARY_ERROR_MESSAGES[SUMMARY_UNKNOWN_CODE];
}

export class FenzSafeSummaryDraftError extends Error {
  readonly code: string;
  constructor(code: string) {
    const known = Object.prototype.hasOwnProperty.call(SUMMARY_ERROR_MESSAGES, code) ? code : SUMMARY_UNKNOWN_CODE;
    super(summaryErrorMessage(known));
    this.code = known;
    this.name = 'FenzSafeSummaryDraftError';
  }
}

/** What the owner confirmed: bound at confirmation and never updated afterwards. */
export type SummaryDraftTarget = {
  scope: string;
  matterId: string;
  documentId: string;
  docVersion: number;
  contentSha256: string;
};

export type SummaryStatement = {statement: string; page: number; excerpt: string; citationStatus: typeof UNVERIFIED_CITATION};

export type ValidatedSummaryDraft = {
  notice: string;
  source: {documentId: string; docVersion: number; contentSha256: string};
  model: string;
  promptVersion: string;
  statements: SummaryStatement[];
  warnings: string[];
};

const SCOPE_PATTERN = /^[A-Za-z0-9-]{1,64}:[A-Za-z0-9-]{1,64}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;

export function isValidSummaryScope(scope: unknown): scope is string {
  return typeof scope === 'string' && SCOPE_PATTERN.test(scope);
}

/** Locally checks a target before any network request; false means refuse with no fetch. */
export function isValidSummaryTarget(target: SummaryDraftTarget): boolean {
  return isValidSummaryScope(target.scope) && UUID_PATTERN.test(target.matterId) && UUID_PATTERN.test(target.documentId)
    && Number.isInteger(target.docVersion) && target.docVersion >= 1 && SHA256_PATTERN.test(target.contentSha256);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
}

function cleanText(value: unknown, max: number): string {
  if (typeof value !== 'string' || CONTROL_CHARACTERS.test(value)) throw new FenzSafeSummaryDraftError(SUMMARY_UNVERIFIABLE_CODE);
  const trimmed = value.trim();
  if (trimmed === '' || Array.from(trimmed).length > max) throw new FenzSafeSummaryDraftError(SUMMARY_UNVERIFIABLE_CODE);
  return trimmed;
}

const RESPONSE_KEYS = ['unconfirmed_ai_draft', 'requires_owner_page_confirmation', 'excerpts_checked_against_document', 'notice', 'source', 'model', 'prompt_version', 'statements', 'warnings'] as const;
const STATEMENT_KEYS = ['statement', 'page', 'excerpt', 'citation_status'] as const;
const SOURCE_KEYS = ['document_id', 'doc_version', 'content_sha256'] as const;

/**
 * Strict boundary for the server's draft. Anything that does not exactly carry all three verification
 * flags, the unverified label on every statement, bounded clean text and provenance bound to the
 * confirmed target is refused as a whole. Display and Copy use only the returned object.
 */
export function parseSummaryDraft(body: unknown, target: SummaryDraftTarget): ValidatedSummaryDraft {
  const fail = () => new FenzSafeSummaryDraftError(SUMMARY_UNVERIFIABLE_CODE);
  if (!isPlainObject(body) || !exactKeys(body, RESPONSE_KEYS)) throw fail();
  if (body.unconfirmed_ai_draft !== true || body.requires_owner_page_confirmation !== true || body.excerpts_checked_against_document !== false) throw fail();
  const source = body.source;
  if (!isPlainObject(source) || !exactKeys(source, SOURCE_KEYS)) throw fail();
  if (source.document_id !== target.documentId || source.doc_version !== target.docVersion || source.content_sha256 !== target.contentSha256) throw fail();
  if (!Array.isArray(body.statements) || body.statements.length > SUMMARY_MAX_STATEMENTS) throw fail();
  if (!Array.isArray(body.warnings) || body.warnings.length > SUMMARY_MAX_WARNINGS) throw fail();
  const statements = body.statements.map((item): SummaryStatement => {
    if (!isPlainObject(item) || !exactKeys(item, STATEMENT_KEYS) || item.citation_status !== UNVERIFIED_CITATION) throw fail();
    if (typeof item.page !== 'number' || !Number.isInteger(item.page) || item.page < 1 || item.page > SUMMARY_MAX_PAGE) throw fail();
    return {
      statement: cleanText(item.statement, SUMMARY_MAX_STATEMENT_CHARS),
      page: item.page,
      excerpt: cleanText(item.excerpt, SUMMARY_MAX_EXCERPT_CHARS),
      citationStatus: UNVERIFIED_CITATION,
    };
  });
  const warnings = body.warnings.map(warning => cleanText(warning, SUMMARY_MAX_WARNING_CHARS));
  return {
    notice: cleanText(body.notice, SUMMARY_MAX_NOTICE_CHARS),
    source: {documentId: target.documentId, docVersion: target.docVersion, contentSha256: target.contentSha256},
    model: cleanText(body.model, 100),
    promptVersion: cleanText(body.prompt_version, 100),
    statements,
    warnings,
  };
}

/**
 * POSTs one summary-draft request for exactly the confirmed target. Refuses locally, with no fetch,
 * when the real adapter is not bound or the target/scope is missing or malformed. Never retries.
 */
export async function requestSummaryDraft(
  target: SummaryDraftTarget,
  options: {adapterReal: boolean; signal?: AbortSignal; fetchImpl?: typeof fetch},
): Promise<ValidatedSummaryDraft> {
  if (!options.adapterReal || !isValidSummaryTarget(target)) throw new FenzSafeSummaryDraftError(SUMMARY_UNAVAILABLE_CODE);
  const fetchImpl = options.fetchImpl ?? fetch;
  const response = await fetchImpl(`/api/v1/fenzsafe/matters/${target.matterId}/documents/${target.documentId}/summary-draft`, {
    method: 'POST',
    signal: options.signal,
    credentials: 'same-origin',
    headers: {'Accept': 'application/json', 'Content-Type': 'application/json', 'X-ProFenz-CSRF': csrfToken(), 'X-ProFenz-Expected-Scope': target.scope},
    body: '{}',
  });
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    throw new FenzSafeSummaryDraftError(response.ok ? SUMMARY_UNVERIFIABLE_CODE : SUMMARY_UNKNOWN_CODE);
  }
  if (!response.ok) {
    const code = isPlainObject(parsed) && typeof parsed.code === 'string' ? parsed.code : SUMMARY_UNKNOWN_CODE;
    throw new FenzSafeSummaryDraftError(code);
  }
  return parseSummaryDraft(parsed, target);
}

/** Plain-text copy built only from the validated draft: full notice, provenance and unverified labels. */
export function summaryCopyText(documentName: string, draft: ValidatedSummaryDraft): string {
  const lines = [
    'UNCONFIRMED AI DRAFT — NOT VERIFIED',
    draft.notice,
    '',
    `Source: ${documentName} (version ${draft.source.docVersion}, SHA-256 ${draft.source.contentSha256.slice(0, 12)}…)`,
    '',
  ];
  draft.statements.forEach((item, index) => {
    lines.push(`${index + 1}. ${item.statement}`);
    lines.push(`   Unverified quote — check page ${item.page}: "${item.excerpt}"`);
  });
  if (draft.warnings.length > 0) {
    lines.push('', 'Reading notes (unverified):');
    for (const warning of draft.warnings) lines.push(`- ${warning}`);
  }
  return lines.join('\n');
}
